import authService from '@/services/auth/auth';
import { apiRoutes } from '@/services/config';

export type SlotStatus = 'empty' | 'declared' | 'evidenced';

export type Slot = {
  status: SlotStatus;
  snippets: string[];
  declaration_only: boolean;
  prompted_spontaneously: boolean;
};

export type CoverageState = {
  slots: Record<string, Slot>;
  turnCount: number;
  newEvidenceLastTurn: boolean;
  consecutiveEmptyTurns: number;
  evidenceQuality: 'strong' | 'supported' | 'weak' | 'insufficient';
  workContext: string;
};

export type AssessmentResult = {
  competency: string;
  assessedLevel: number | null;
  levelName: string | null;
  confidence: string;
  evidenceQuality: string;
  detectedBehavioralIndicators: string[];
  missingInformation: string[];
  justification: string;
  developmentRecommendation: string | null;
  requestMoreEvidence: string | null;
};

export type TranscriptMessage = {
  role: 'bot' | 'user';
  content: string;
  turn: number;
};

export type SessionLanguage = 'pl' | 'en';

export type Session = {
  id: number;
  competency: string;
  status: string;
  language: SessionLanguage;
  transcript: TranscriptMessage[];
  coverageState: CoverageState;
  result: AssessmentResult | null;
  createdAt: string;
};

export type SessionListItem = {
  id: number;
  competency: string;
  status: string;
  language: SessionLanguage;
  createdAt: string;
  updatedAt: string;
};

export type StartSessionResponse = {
  id: number;
  botMessage: string;
};

export type SendMessageResponse =
  | { botMessage: string; status?: never; result?: never }
  | { status: 'EVALUATING'; botMessage: string; result?: never }
  | { status: 'COMPLETED'; result: AssessmentResult; botMessage: string };

export const startSession = async (
  language: SessionLanguage = 'pl'
): Promise<StartSessionResponse> => {
  const { data } = await authService.post(
    apiRoutes.competencyAssessment.sessions._root,
    { language }
  );
  return data;
};

export const sendMessage = async (
  sessionId: number,
  content: string
): Promise<SendMessageResponse> => {
  const { data } = await authService.post(
    apiRoutes.competencyAssessment.sessions.session(sessionId).messages,
    { content }
  );
  return data;
};

export const getSession = async (sessionId: number): Promise<Session> => {
  const { data } = await authService.get(
    apiRoutes.competencyAssessment.sessions.session(sessionId)._root
  );
  return data;
};

export const getSessions = async (): Promise<SessionListItem[]> => {
  const { data } = await authService.get(
    apiRoutes.competencyAssessment.sessions._root
  );
  return data.results ?? data;
};
